/**
 * Project Approvals — W3-A · B7.
 *
 * Route: /workflows/approvals
 * Perm : project:approve
 *
 * Lists workflow instances the caller can act on (approve/reject).
 * Self-initiated requests are visually flagged so the caller knows
 * approval is blocked. Clicking a row opens the RequestDetailDrawer;
 * inline Approve/Reject buttons open the corresponding dialogs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ArrowClockwise, CheckCircle, XCircle, Warning, ClipboardText,
} from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { WorkflowStatusBadge, formatDT } from "./workflowUi";
import RequestDetailDrawer from "./components/RequestDetailDrawer";
import ApproveDialog from "./components/ApproveDialog";
import RejectDialog from "./components/RejectDialog";
import { PageLoading } from "../projects/components/ui";


const TAB_ALL = "pending";
const TAB_ALL_STATUSES = "all";


export default function ApprovalsQueue() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [tab, setTab] = useState(TAB_ALL);
    const [openInstanceId, setOpenInstanceId] = useState(null);
    const [approveTarget, setApproveTarget] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("mine", "false");
            params.set("workflow_code", "project_initiation");
            params.set("limit", "100");
            if (tab === TAB_ALL) params.set("status", "pending");
            const r = await api.get(`/workflows/instances?${params}`);
            setItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load approvals queue");
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const needle = q.trim().toLowerCase();
        return items.filter((it) => {
            const name = (it.payload?.name || "").toLowerCase();
            const ref = (it.workflow_instance_id || "").toLowerCase();
            const initiator = (it.initiator_user_id || "").toLowerCase();
            return name.includes(needle)
                || ref.includes(needle)
                || initiator.includes(needle);
        });
    }, [items, q]);

    const currentUserId = user?.user_id || user?.id;

    function isOwnRequest(it) {
        return it.initiator_user_id && it.initiator_user_id === currentUserId;
    }

    return (
        <div className="space-y-6" data-testid="approvals-queue-page">
            <PageHeader
                eyebrow="Delivery"
                title="Project Approvals"
                description="Review Project Requests awaiting approval. Approve to create an Active Project, or reject with a reason so the initiator can act on the feedback."
                actions={
                    <Button
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        data-testid="approvals-refresh-btn"
                    >
                        <ArrowClockwise
                            className={"w-4 h-4 mr-1 " + (loading ? "animate-spin" : "")}
                        />
                        Refresh
                    </Button>
                }
            />

            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => setTab(TAB_ALL)}
                    className={
                        "px-3 py-1 rounded-full border text-sm transition " +
                        (tab === TAB_ALL
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                    }
                    data-testid="approvals-tab-pending"
                >
                    Pending Approval
                </button>
                <button
                    type="button"
                    onClick={() => setTab(TAB_ALL_STATUSES)}
                    className={
                        "px-3 py-1 rounded-full border text-sm transition " +
                        (tab === TAB_ALL_STATUSES
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                    }
                    data-testid="approvals-tab-all"
                >
                    All Requests
                </button>
                <div className="flex-1 min-w-[200px]">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by project name, reference, or initiator…"
                        data-testid="approvals-search"
                    />
                </div>
            </div>

            {loading ? (
                <PageLoading
                    label="Loading requests…"
                    testId="approvals-loading"
                />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<ClipboardText className="w-6 h-6" />}
                    title={
                        items.length === 0
                            ? (tab === TAB_ALL
                                ? "No requests waiting on you"
                                : "No requests yet")
                            : "No matches"
                    }
                    description={
                        items.length === 0
                            ? "Project Requests submitted by initiators appear here for review."
                            : "Try clearing the search or switching tabs."
                    }
                />
            ) : (
                <div
                    className="overflow-x-auto border border-slate-200 rounded-md"
                    data-testid="approvals-table-wrapper"
                >
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="text-left px-3 py-2">Reference</th>
                                <th className="text-left px-3 py-2">Project</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-left px-3 py-2">Initiator</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Submitted</th>
                                <th className="text-right px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="approvals-table-body">
                            {filtered.map((it) => {
                                const own = isOwnRequest(it);
                                const isPending = it.status === "pending_s1";
                                return (
                                    <tr
                                        key={it.workflow_instance_id}
                                        className="border-t border-slate-100 hover:bg-slate-50"
                                        data-testid={`approvals-row-${it.workflow_instance_id}`}
                                    >
                                        <td
                                            className="px-3 py-2 font-mono text-xs text-slate-700 cursor-pointer"
                                            onClick={() => setOpenInstanceId(it.workflow_instance_id)}
                                        >
                                            {it.workflow_instance_id}
                                        </td>
                                        <td
                                            className="px-3 py-2 text-slate-900 cursor-pointer"
                                            onClick={() => setOpenInstanceId(it.workflow_instance_id)}
                                        >
                                            {it.payload?.name || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-slate-700 capitalize">
                                            {it.payload?.project_type || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-slate-700">
                                            <div className="flex items-center gap-1">
                                                <span
                                                    className="truncate max-w-[10rem]"
                                                    data-testid={`approvals-initiator-${it.workflow_instance_id}`}
                                                >
                                                    {it.initiator_user_id}
                                                </span>
                                                {own ? (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-800 border border-amber-200"
                                                        data-testid={`approvals-own-badge-${it.workflow_instance_id}`}
                                                        title="Self-approval is not allowed"
                                                    >
                                                        <Warning className="w-3 h-3" />
                                                        You submitted this
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <WorkflowStatusBadge status={it.status} />
                                        </td>
                                        <td className="px-3 py-2 text-slate-500 text-xs">
                                            {formatDT(it.created_at)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {isPending ? (
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="border-red-500 text-red-700 hover:bg-red-50"
                                                        disabled={own}
                                                        title={own ? "Self-approval blocked" : "Reject request"}
                                                        onClick={() => setRejectTarget(it)}
                                                        data-testid={`approvals-reject-btn-${it.workflow_instance_id}`}
                                                    >
                                                        <XCircle className="w-4 h-4 mr-1" />
                                                        Reject
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        disabled={own}
                                                        title={own ? "Self-approval blocked" : "Approve request"}
                                                        onClick={() => setApproveTarget(it)}
                                                        data-testid={`approvals-approve-btn-${it.workflow_instance_id}`}
                                                    >
                                                        <CheckCircle className="w-4 h-4 mr-1" />
                                                        Approve
                                                    </Button>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <RequestDetailDrawer
                open={!!openInstanceId}
                instanceId={openInstanceId}
                onClose={() => setOpenInstanceId(null)}
                canApprove={true}
                onApproveClicked={(inst) => {
                    if (inst.initiator_user_id === currentUserId) {
                        toast.error("You cannot approve your own request.");
                        return;
                    }
                    setOpenInstanceId(null);
                    setApproveTarget(inst);
                }}
                onRejectClicked={(inst) => {
                    if (inst.initiator_user_id === currentUserId) {
                        toast.error("You cannot reject your own request.");
                        return;
                    }
                    setOpenInstanceId(null);
                    setRejectTarget(inst);
                }}
            />

            <ApproveDialog
                open={!!approveTarget}
                instance={approveTarget}
                onClose={() => setApproveTarget(null)}
                onApproved={() => {
                    setApproveTarget(null);
                    load();
                }}
            />
            <RejectDialog
                open={!!rejectTarget}
                instance={rejectTarget}
                onClose={() => setRejectTarget(null)}
                onRejected={() => {
                    setRejectTarget(null);
                    load();
                }}
            />
        </div>
    );
}
